import React, { useState, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Stack,
  Text,
  useToast,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Checkbox,
  Divider,
  Switch,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { FiUpload } from "react-icons/fi";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useBusinessUnit } from "../../context/BusinessUnitContext";
import { useTranslation } from "react-i18next";
import { PageHeader, Panel, StatusBadge } from "../../components/Portal/ui";

const SectionHeading = ({ title, description }) => (
  <Box pr={{ base: 0, md: 8 }} minW={{ md: "220px" }}>
    <Heading
      as="h3"
      fontSize="14px"
      letterSpacing="-0.02em"
      color="portal.text"
      sx={{ marginBottom: "6px !important" }}
    >
      {title}
    </Heading>
    <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
      {description}
    </Text>
  </Box>
);

const SectionCard = ({ children }) => (
  <Panel>
    <Box p="20px">{children}</Box>
  </Panel>
);

const primaryBtnProps = {
  variant: "primary",
  size: "sm",
  h: "31px",
  px: "14px",
  fontSize: "12px",
  w: "fit-content",
};

// Module-grouped notification event definitions (Phase 4)
const NOTIFICATION_MODULES = [
  {
    key: "ASAP_PILOT",
    label: "ASAP Pilot",
    labelKey: "profile.notifications.modules.pilot",
    events: [
      { key: "NEED_CREATED", label: "Need created", labelKey: "profile.notifications.eventsNeedCreated" },
      { key: "NEED_COVERED", label: "Need covered / taken over", labelKey: "profile.notifications.eventsNeedCovered" },
      { key: "CLIENT_VALIDATED_PROPOSAL", label: "Client validated a proposal", labelKey: "profile.notifications.eventsClientValidated" },
      { key: "MATCHES_IDENTIFIED", label: "Matches identified for a need", labelKey: "profile.notifications.eventsMatchesIdentified" },
      { key: "PROFILE_PRESENTED", label: "Profile presented to client", labelKey: "profile.notifications.eventsProfilePresented" },
    ],
  },
  {
    key: "ASAP_CV",
    label: "ASAP Skills",
    labelKey: "profile.notifications.modules.skills",
    events: [
      { key: "ASSESSMENT_STARTED", label: "Assessment started", labelKey: "profile.notifications.eventsAssessmentStarted" },
      { key: "ASSESSMENT_COMPLETED", label: "Assessment completed", labelKey: "profile.notifications.eventsAssessmentCompleted" },
    ],
  },
  {
    key: "ASAP_PARTNERS",
    label: "ASAP Partners",
    labelKey: "profile.notifications.modules.partners",
    events: [
      { key: "APPLICATION_HIGH_MATCH", label: "High-scoring application received", labelKey: "profile.notifications.eventsApplicationHighMatch" },
      { key: "CV_RECEIVED", label: "CV / application received", labelKey: "profile.notifications.eventsCvReceived" },
      { key: "NEW_REQUIREMENT_FROM_PARTNER", label: "New requirement from a partner", labelKey: "profile.notifications.eventsNewRequirementFromPartner" },
      { key: "INCOMING_CONSULTANT_FROM_PARTNER", label: "Consultant proposed by a partner", labelKey: "profile.notifications.eventsIncomingConsultantFromPartner" },
    ],
  },
];

const ProfileSettings = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isPwModalOpen, onOpen: openPwModal, onClose: closePwModal } = useDisclosure();
  const { businessUnits, selectedBusinessUnitId, setSelectedBusinessUnitId, isConsultantPortal, selectedBusinessUnit } = useBusinessUnit();
  const { t } = useTranslation("portal");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const fileInputRef = useRef();
  const [jobTitle, setJobTitle] = useState("");
  const [reportingManager, setReportingManager] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // ----------------------------
  // Notification preferences (module-grouped, Phase 4)
  // ----------------------------
  const apiBase = process.env.REACT_APP_BASE_API_URL;

  const [notificationPrefs, setNotificationPrefs] = useState({
    enabled: true,
    channels: { in_app: true, email: false },
    per_event: {},
  });
  const [loadingNotificationPrefs, setLoadingNotificationPrefs] = useState(false);
  const [savingNotificationPrefs, setSavingNotificationPrefs] = useState(false);

  // Helper to check if an event is enabled in the module-grouped per_event structure
  const isEventEnabled = (moduleKey, eventKey) => {
    const modulePrefs = notificationPrefs.per_event?.[moduleKey];
    if (!modulePrefs || modulePrefs[eventKey] === undefined) return true; // default on
    const val = modulePrefs[eventKey];
    if (typeof val === "boolean") return val;
    if (typeof val === "object" && val !== null) return val.enabled !== false;
    return true;
  };

  const setEventEnabled = (moduleKey, eventKey, enabled) => {
    setNotificationPrefs((prev) => ({
      ...prev,
      per_event: {
        ...prev.per_event,
        [moduleKey]: {
          ...(prev.per_event?.[moduleKey] || {}),
          [eventKey]: { enabled },
        },
      },
    }));
  };

  React.useEffect(() => {
    const fetchPrefs = async () => {
      if (!apiBase) return;
      setLoadingNotificationPrefs(true);
      try {
        const res = await axios.get(`${apiBase}/notification-preferences/me`);
        const data = res?.data || {};
        setNotificationPrefs((prev) => ({
          ...prev,
          enabled: data.enabled ?? prev.enabled,
          channels: { ...prev.channels, ...(data.channels || {}) },
          per_event: data.per_event || prev.per_event,
        }));
      } catch (err) {
        // Non-blocking if backend not deployed yet.
      } finally {
        setLoadingNotificationPrefs(false);
      }
    };
    fetchPrefs();
  }, [apiBase]);

  const saveNotificationPrefs = async () => {
    if (!apiBase) {
      toast({
        title: t("profile.notifications.toasts.noApiTitle", { defaultValue: "Unable to save" }),
        description: t("profile.notifications.toasts.noApiDescription", { defaultValue: "API base URL is not configured." }),
        status: "error",
        duration: 4000,
      });
      return;
    }
    setSavingNotificationPrefs(true);
    try {
      await axios.put(`${apiBase}/notification-preferences/me`, notificationPrefs);
      toast({
        title: t("profile.notifications.toasts.savedTitle", { defaultValue: "Notification preferences saved" }),
        status: "success",
        duration: 3000,
      });
    } catch (err) {
      toast({
        title: t("profile.notifications.toasts.saveFailedTitle", { defaultValue: "Save failed" }),
        description: err?.response?.data?.message || err.message,
        status: "error",
        duration: 4000,
      });
    } finally {
      setSavingNotificationPrefs(false);
    }
  };

  // Fetch profile data on mount
  React.useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/auth/users/me`);
        const data = res.data;
        setFirstName(data.first_name || data.firstName || "");
        setLastName(data.last_name || data.lastName || "");
        setEmail(data.email || "");
        setLinkedin(data.linkedin || "");
        setAvatarPreview(data.avatar_url || "");
        setJobTitle(data.job_title || data.jobTitle || "");
        setReportingManager(data.reporting_manager || data.managerName || "");
      } catch (err) {
        console.error("Failed to fetch profile", err);
      }
    };
    fetchProfile();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      const url = `${process.env.REACT_APP_BASE_API_URL}/auth/users/me`;
      if (avatarFile) {
        const formData = new FormData();
        formData.append("avatar", avatarFile);
        formData.append("name", `${firstName} ${lastName}`.trim());
        formData.append("email", email);
        formData.append("linkedin", linkedin);
        const res = await axios.put(url, formData);
        if (res.data?.avatar_url) setAvatarPreview(res.data.avatar_url);
      } else {
        const res = await axios.put(url, {
          name: `${firstName} ${lastName}`.trim(),
          email,
          linkedin,
        });
        if (res.data?.avatar_url) setAvatarPreview(res.data.avatar_url);
      }
      if (avatarFile) {
        setAvatarFile(null);
      }
      toast({ title: t("profile.toasts.profileUpdated"), status: "success", duration: 3000 });
    } catch (err) {
      toast({
        title: t("profile.toasts.profileUpdateFailed"),
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 4000,
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async () => {
    setSavingPassword(true);
    try {
      await axios.post(`${process.env.REACT_APP_BASE_API_URL}/auth/change-password`, {
        currentPassword,
        newPassword,
      });
      toast({ title: t("profile.toasts.passwordUpdated"), status: "success", duration: 3000 });
      openPwModal();
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      toast({
        title: t("profile.toasts.passwordUpdateFailed"),
        description: err.response?.data?.message || err.message,
        status: "error",
        duration: 4000,
      });
    } finally {
      setSavingPassword(false);
    }
  };

  // ── Tab 1: Profile ──
  const renderProfileTab = () => (
    <Stack spacing="12px">
      {/* Profile Information */}
      <SectionCard>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px">
          <SectionHeading
            title={t("profile.sections.profileInfo.title")}
            description={t("profile.sections.profileInfo.description")}
          />
          <Stack spacing="16px">
            {!isConsultantPortal && (
              <Flex align="center" gap="16px">
                <Avatar name={`${firstName} ${lastName}`.trim()} size="lg" bg="portal.active" color="portal.primary" src={avatarPreview} />
                <Button leftIcon={<Icon as={FiUpload} />} variant="secondary" size="sm" h="31px" fontSize="12px" onClick={() => fileInputRef.current?.click()}>
                  {t("profile.buttons.uploadPhoto")}
                </Button>
                <Input type="file" accept="image/*" display="none" ref={fileInputRef} onChange={handleFileChange} />
              </Flex>
            )}
            <FormControl>
              <FormLabel>{t("profile.fields.firstName")}</FormLabel>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} isReadOnly={isConsultantPortal} _readOnly={{ bg: "portal.soft", cursor: "not-allowed" }} />
            </FormControl>
            <FormControl>
              <FormLabel>{t("profile.fields.lastName")}</FormLabel>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} isReadOnly={isConsultantPortal} _readOnly={{ bg: "portal.soft", cursor: "not-allowed" }} />
            </FormControl>
            <FormControl>
              <FormLabel>{t("profile.fields.email")}</FormLabel>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} isReadOnly={isConsultantPortal} _readOnly={{ bg: "portal.soft", cursor: "not-allowed" }} />
            </FormControl>
            {!isConsultantPortal && (
              <Button {...primaryBtnProps} onClick={handleProfileSave} isLoading={savingProfile}>
                {t("profile.buttons.save")}
              </Button>
            )}
          </Stack>
        </SimpleGrid>
      </SectionCard>

      {/* Password */}
      <SectionCard>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px">
          <SectionHeading title={t("profile.sections.password.title")} description={t("profile.sections.password.description")} />
          <Stack spacing="16px">
            <FormControl>
              <FormLabel>{t("profile.fields.currentPassword")}</FormLabel>
              <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>{t("profile.fields.newPassword")}</FormLabel>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </FormControl>
            <Button {...primaryBtnProps} onClick={handlePasswordSave} isLoading={savingPassword}>
              {t("profile.buttons.save")}
            </Button>
          </Stack>
        </SimpleGrid>
      </SectionCard>

      {/* LinkedIn */}
      {!isConsultantPortal && (
        <SectionCard>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px">
            <SectionHeading title={t("profile.sections.linkedin.title")} description={t("profile.sections.linkedin.description")} />
            <Stack spacing="16px">
              <FormControl>
                <Input placeholder={t("profile.fields.linkedinUrl")} value={linkedin} onChange={(e) => setLinkedin(e.target.value)} />
              </FormControl>
              <Button {...primaryBtnProps} onClick={handleProfileSave} isLoading={savingProfile}>
                {t("profile.buttons.save")}
              </Button>
            </Stack>
          </SimpleGrid>
        </SectionCard>
      )}
    </Stack>
  );

  // ── Tab 2: Notifications ──
  const renderNotificationsTab = () => (
    <Stack spacing="12px">
      <SectionCard>
        <Stack spacing="20px">
          <Flex align="center" justify="space-between">
            <Text fontWeight="700" color="portal.text" sx={{ marginBottom: "0 !important" }} >
              {t("profile.notifications.globalToggle", { defaultValue: "Enable notifications" })}
            </Text>
            <Switch
              colorScheme="teal"
              isChecked={!!notificationPrefs.enabled}
              isDisabled={loadingNotificationPrefs}
              onChange={(e) => setNotificationPrefs((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
          </Flex>

          <Box>
            <Text fontWeight="700" color="portal.text" mb="8px" sx={{ marginBottom: "8px !important" }}>
              {t("profile.notifications.channels", { defaultValue: "Channels" })}
            </Text>
            <Stack spacing="8px">
              <Checkbox
                isChecked={!!notificationPrefs.channels?.in_app}
                isDisabled={loadingNotificationPrefs || !notificationPrefs.enabled}
                onChange={(e) => setNotificationPrefs((prev) => ({ ...prev, channels: { ...(prev.channels || {}), in_app: e.target.checked } }))}
              >
                {t("profile.notifications.channelsInApp", { defaultValue: "In-app notifications" })}
              </Checkbox>
              <Checkbox
                isChecked={!!notificationPrefs.channels?.email}
                isDisabled={loadingNotificationPrefs || !notificationPrefs.enabled}
                onChange={(e) => setNotificationPrefs((prev) => ({ ...prev, channels: { ...(prev.channels || {}), email: e.target.checked } }))}
              >
                {t("profile.notifications.channelsEmail", { defaultValue: "Email notifications" })}
              </Checkbox>
            </Stack>
          </Box>

          <Divider borderColor="portal.line" />

          <Box>
            <Text fontWeight="700" color="portal.text" mb="12px" sx={{ marginBottom: "12px !important" }}>
              {t("profile.notifications.eventsByModule", { defaultValue: "Events by module" })}
            </Text>
            <Accordion allowMultiple defaultIndex={NOTIFICATION_MODULES.map((_, i) => i)}>
              {NOTIFICATION_MODULES.map((mod) => (
                <AccordionItem key={mod.key} borderWidth="1px" borderColor="portal.line" borderRadius="10px" mb="8px">
                  <AccordionButton py="12px">
                    <Box flex="1" textAlign="left" fontWeight="700" fontSize="13px" color="portal.text">
                      {t(mod.labelKey, { defaultValue: mod.label })}
                    </Box>
                    <AccordionIcon color="portal.muted" />
                  </AccordionButton>
                  <AccordionPanel pb="16px">
                    <Stack spacing="8px">
                      {mod.events.map((evt) => (
                        <Checkbox
                          key={evt.key}
                          colorScheme="teal"
                          isChecked={isEventEnabled(mod.key, evt.key)}
                          isDisabled={loadingNotificationPrefs || !notificationPrefs.enabled}
                          onChange={(e) => setEventEnabled(mod.key, evt.key, e.target.checked)}
                        >
                          {t(evt.labelKey, { defaultValue: evt.label })}
                        </Checkbox>
                      ))}
                      {mod.events.length === 0 && (
                        <Text fontSize="12px" color="portal.muted">
                          {t("profile.notifications.noEvents", { defaultValue: "No configurable events for this module." })}
                        </Text>
                      )}
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Box>

          <Button
            {...primaryBtnProps}
            onClick={saveNotificationPrefs}
            isLoading={savingNotificationPrefs}
            isDisabled={loadingNotificationPrefs}
          >
            {t("profile.notifications.save", { defaultValue: "Save preferences" })}
          </Button>

          {!apiBase && (
            <Text fontSize="12px" color="portal.danger">
              {t("profile.notifications.apiWarning", {
                defaultValue: "Notifications preferences API is not configured in this environment.",
              })}
            </Text>
          )}
        </Stack>
      </SectionCard>
    </Stack>
  );

  // ── Tab 3: Organization ──
  const renderOrganizationTab = () => (
    <Stack spacing="12px">
      {/* Business Units */}
      <SectionCard>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px">
          <SectionHeading title={t("profile.sections.businessUnits.title")} description={t("profile.sections.businessUnits.description")} />
          <Stack spacing="8px">
            {businessUnits.length === 0 && <Text fontSize="13px" color="portal.muted">{t("profile.fields.noBusinessUnits")}</Text>}
            {businessUnits.map((bu) => (
              <Flex key={bu.id} align="center" justify="space-between" gap="8px" px="12px" py="10px" borderWidth="1px" borderColor="portal.line" borderRadius="10px">
                <Text fontWeight="700" fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>{bu.unit_name || bu.unit_code}</Text>
                <Flex align="center" gap="8px" flexShrink={0}>
                  <StatusBadge
                    label={bu.role}
                    colorScheme={bu.id === Number(selectedBusinessUnitId) ? "green" : "gray"}
                  />
                  {bu.id !== Number(selectedBusinessUnitId) && (
                    <Button variant="secondary" size="xs" h="25px" fontSize="11px" onClick={() => setSelectedBusinessUnitId(bu.id)}>
                      {t("profile.buttons.selectBU")}
                    </Button>
                  )}
                </Flex>
              </Flex>
            ))}
          </Stack>
        </SimpleGrid>
      </SectionCard>

      {/* Role */}
      <SectionCard>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px">
          <SectionHeading title={t("profile.sections.role.title")} description={t("profile.sections.role.description")} />
          <Stack spacing="16px">
            <FormControl isDisabled>
              <FormLabel>{t("profile.fields.jobTitle")}</FormLabel>
              <Input value={jobTitle} placeholder={t("profile.fields.notSet")} />
            </FormControl>
            <FormControl isDisabled>
              <FormLabel>{t("profile.fields.department")}</FormLabel>
              <Input value={selectedBusinessUnit?.unit_name || selectedBusinessUnit?.unit_code || ""} placeholder={t("profile.fields.notSet")} />
            </FormControl>
            <FormControl isDisabled>
              <FormLabel>{t("profile.fields.reportingManager")}</FormLabel>
              <Input value={reportingManager} placeholder={t("profile.fields.notSet")} />
            </FormControl>
          </Stack>
        </SimpleGrid>
      </SectionCard>
    </Stack>
  );

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="PR"
        eyebrow={t("profile.eyebrow", "Account")}
        title={t("profile.pageTitle")}
      />

      <Box p="20px" maxW="1100px">
        {isConsultantPortal ? (
          // Consultant portal: only profile tab, no tabs UI needed
          renderProfileTab()
        ) : (
          <Tabs colorScheme="teal" variant="enclosed">
            <TabList>
              <Tab>{t("profile.tabs.profile", { defaultValue: "Profile" })}</Tab>
              <Tab>{t("profile.tabs.notifications", { defaultValue: "Notifications" })}</Tab>
              <Tab>{t("profile.tabs.organization", { defaultValue: "Organization" })}</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0} pt="16px">{renderProfileTab()}</TabPanel>
              <TabPanel px={0} pt="16px">{renderNotificationsTab()}</TabPanel>
              <TabPanel px={0} pt="16px">{renderOrganizationTab()}</TabPanel>
            </TabPanels>
          </Tabs>
        )}
      </Box>

      {/* Logout required modal */}
      <Modal isOpen={isPwModalOpen} onClose={closePwModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("profile.passwordModal.title")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>{t("profile.passwordModal.body")}</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="primary"
              onClick={() => {
                closePwModal();
                logout();
                navigate("/login");
              }}
            >
              {t("profile.passwordModal.ok")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProfileSettings;
